import React, {useState, useMemo} from 'react';

import {BackendJob, createEditedJob} from "components/EditJob";
import {JobTitle} from "components/JobTitle";
import {ApplicationsList} from "components/ApplicationsList";
import {Location} from "components/BillingLocationSelector";
import {JobBoardDetailTab, Application, ApplicationStatus} from "./types";
import JobInfoModal from "./JobInfo/JobInfo";

import {Container, StyledSectionTabs} from './JobBoardDetails.styles';

interface JobBoardDetailsProps {
    job: BackendJob;
    applications: Application[];
    locations: Location[];
    onApplicationClick: (application: Application) => void;
}

const JobBoardDetails = ({job, applications, onApplicationClick}: JobBoardDetailsProps) => {
    const [activeTab, setActiveTab] = useState<JobBoardDetailTab>(JobBoardDetailTab.newOrViewed);
    const [isJobInfoOpen, setIsJobInfoOpen] = useState(false);

    const handleJobInfoModal = () => {
        setIsJobInfoOpen(!isJobInfoOpen);
    };

    const editedJob = createEditedJob(job);

    const jobLocation = editedJob.location;

    const {newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates} = useMemo(
        () =>
            applications.reduce<{
                newCandidates: Application[];
                shortlistedCandidates: Application[];
                declinedCandidates: Application[];
                viewedCandidates: Application[];
            }>((acc, application) => {
                if (application.status === ApplicationStatus.posted) {
                    acc.newCandidates.push(application);
                } else if (application.status === ApplicationStatus.shortlisted) {
                    acc.shortlistedCandidates.push(application);
                } else if (application.status === ApplicationStatus.declined) {
                    acc.declinedCandidates.push(application);
                } else {
                    acc.viewedCandidates.push(application)
                }

                return acc;
            },
            {newCandidates: [], shortlistedCandidates: [], declinedCandidates: [], viewedCandidates: []}
        ),
        [applications]
    )

    const tabs = [
        {label: `New/Viewed(${newCandidates.length + viewedCandidates.length})`, onClick: () => setActiveTab(JobBoardDetailTab.newOrViewed), isActive: activeTab === JobBoardDetailTab.newOrViewed},
        {label: `Shortlisted(${shortlistedCandidates.length})`, onClick: () => setActiveTab(JobBoardDetailTab.shortlisted), isActive: activeTab === JobBoardDetailTab.shortlisted},
        {label: `Hide(${declinedCandidates.length})`, onClick: () => setActiveTab(JobBoardDetailTab.hideCandidates), isActive: activeTab === JobBoardDetailTab.hideCandidates}
    ];

    return (
        <Container>
            <JobTitle
                title={editedJob.title || ''}
                {...(jobLocation ? { location: jobLocation } : {})}
                job={job}
            />
            <StyledSectionTabs tabs={tabs} />
            {activeTab === JobBoardDetailTab.newOrViewed ? (
                <ApplicationsList onApplicationClick={onApplicationClick} applicationType={'newOrViewed'} sortedApplications={{newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates}}/>
            ) : null}
            {activeTab === JobBoardDetailTab.shortlisted ? (
                <ApplicationsList onApplicationClick={onApplicationClick} applicationType={'shortlisted'} sortedApplications={{newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates}}/>
            ) : null}
            {activeTab === JobBoardDetailTab.hideCandidates ? (
                <ApplicationsList onApplicationClick={onApplicationClick} applicationType={'hideCandidates'} sortedApplications={{newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates}}/>
            ) : null}
        
        </Container>
    );
};

export default JobBoardDetails;
